import { useCallback, useEffect, useState } from 'react';
import { getPilotChecks, passPilotCheck, updatePilotChecks } from '../../api';
import { PilotCheckItem, PilotChecks } from '../../types';
import { useFeedback } from '../../hooks/useFeedback';
import { DateFormat, DateFormatStyle } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import './PilotChecksPanel.css';

type CheckKey = 'ppl' | 'vlek' | 'annual' | 'skills';
type CheckStatusKind = 'ok' | 'soon' | 'overdue' | 'missing' | 'needs_birth';

type Props = {
  userId: number;
  dateFormat?: DateFormatStyle;
  canEdit?: boolean;
};

const CHECKS: {
  key: CheckKey;
  title: string;
  hint: string;
  dateLabel: string;
  kind: 'license' | 'check';
}[] = [
  {
    key: 'ppl',
    title: 'Свидетельство частного пилота',
    hint: 'Дата выдачи, без срока действия',
    dateLabel: 'Дата выдачи',
    kind: 'license',
  },
  {
    key: 'vlek',
    title: 'ВЛЭК',
    hint: 'До 40 лет — раз в 5 лет; с 40 — раз в 2 года',
    dateLabel: 'Дата прохождения',
    kind: 'check',
  },
  {
    key: 'skills',
    title: 'Проверка навыков',
    hint: 'Каждые 2 года',
    dateLabel: 'Дата прохождения',
    kind: 'check',
  },
  {
    key: 'annual',
    title: 'Годовой осмотр',
    hint: 'До конца того же месяца следующего года',
    dateLabel: 'Дата прохождения',
    kind: 'check',
  },
];

const getToday = () => new Date().toISOString().split('T')[0];

const emptyChecks = (): PilotChecks => ({
  birth_date: null,
  ppl: { last_date: null, next_date: null, overdue: false, days_left: null },
  vlek: { last_date: null, next_date: null, overdue: false, days_left: null },
  annual: { last_date: null, next_date: null, overdue: false, days_left: null },
  skills: { last_date: null, next_date: null, overdue: false, days_left: null },
});

const checkStatusKind = (item: PilotCheckItem): CheckStatusKind => {
  if (item.needs_birth_date) return 'needs_birth';
  if (item.overdue) return 'overdue';
  if (!item.last_date) return 'missing';
  if (item.days_left != null && item.days_left <= 30) return 'soon';
  return 'ok';
};

const statusText = (
  item: PilotCheckItem,
  dateFormat: DateFormatStyle,
  kind: 'license' | 'check' = 'check'
) => {
  if (item.needs_birth_date) {
    return 'Укажите дату рождения для расчёта срока';
  }
  if (!item.last_date) {
    return kind === 'license' ? 'Ещё не указано' : 'Ещё не пройдено';
  }
  if (!item.next_date) {
    const prefix = kind === 'license' ? 'Выдано' : 'Пройдено';
    return `${prefix}: ${DateFormat(item.last_date, dateFormat)}`;
  }
  const next = DateFormat(item.next_date, dateFormat);
  if (item.overdue) {
    return `Просрочено с ${next}`;
  }
  if (item.days_left != null && item.days_left <= 30) {
    return `До ${next} (осталось ${item.days_left} дн.)`;
  }
  return `Следующий срок: ${next}`;
};

const PilotChecksPanel = ({
  userId,
  dateFormat = 'dot',
  canEdit = true,
}: Props) => {
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const [checks, setChecks] = useState<PilotChecks>(emptyChecks());
  const [birthDate, setBirthDate] = useState('');
  const [drafts, setDrafts] = useState<Record<CheckKey, string>>({
    ppl: '',
    vlek: '',
    annual: '',
    skills: '',
  });
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getPilotChecks(userId);
      setChecks(res.data);
      setBirthDate(res.data.birth_date || '');
      setDrafts({
        ppl: res.data.ppl?.last_date || '',
        vlek: res.data.vlek.last_date || '',
        annual: res.data.annual.last_date || '',
        skills: res.data.skills.last_date || '',
      });
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки проверок'));
    }
  }, [userId, showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSaveBirth = async () => {
    if (!birthDate) {
      showError('Укажите дату рождения');
      return;
    }
    setSaving('birth');
    try {
      const res = await updatePilotChecks(userId, { birth_date: birthDate });
      setChecks(res.data);
      setBirthDate(res.data.birth_date || '');
      showSuccess('Дата рождения сохранена');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка сохранения даты рождения'));
    }
    setSaving(null);
  };

  const handleSaveCheck = async (key: CheckKey) => {
    if (key === 'vlek' && !checks.birth_date) {
      showError('Сначала укажите дату рождения');
      return;
    }
    const value = drafts[key];
    if (!value) {
      showError(
        key === 'ppl' ? 'Укажите дату выдачи' : 'Укажите дату прохождения'
      );
      return;
    }
    setSaving(key);
    try {
      const res = await passPilotCheck(userId, key, value);
      setChecks(res.data);
      setDrafts({
        ppl: res.data.ppl?.last_date || '',
        vlek: res.data.vlek.last_date || '',
        annual: res.data.annual.last_date || '',
        skills: res.data.skills.last_date || '',
      });
      showSuccess(
        key === 'ppl' ? 'Дата выдачи сохранена' : 'Дата прохождения сохранена'
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка сохранения даты'));
    }
    setSaving(null);
  };

  const hasBirthDate = Boolean(checks.birth_date);

  return (
    <div className="pilot-checks-panel">
      {feedbackNode}

      <div className="pilot-checks-list">
        <article
          className={`pilot-check-card${hasBirthDate ? ' accent-ok' : ' accent-missing'}`}
        >
          <div className="pilot-check-header">
            <h3 className="pilot-check-title">Дата рождения</h3>
            <p className="pilot-check-hint">
              Нужна для расчёта срока ВЛЭК
            </p>
          </div>
          <p className="pilot-check-status-line">
            {hasBirthDate
              ? `Указана: ${DateFormat(checks.birth_date!, dateFormat)}`
              : 'Ещё не указана'}
          </p>
          {canEdit ? (
            <div className="pilot-check-form">
              <input
                type="date"
                value={birthDate}
                max={getToday()}
                onChange={(e) => setBirthDate(e.target.value)}
                aria-label="Дата рождения"
              />
              <button
                type="button"
                className="btn-check-save"
                disabled={saving === 'birth'}
                onClick={handleSaveBirth}
              >
                {saving === 'birth' ? '…' : 'Сохранить'}
              </button>
            </div>
          ) : null}
        </article>

        {CHECKS.map(({ key, title, hint, dateLabel, kind: itemKind }) => {
          const item = checks[key] || emptyChecks()[key];
          const lockedForBirth = key === 'vlek' && !hasBirthDate;
          const kind = lockedForBirth
            ? 'needs_birth'
            : checkStatusKind(item);
          return (
            <article
              key={key}
              className={`pilot-check-card accent-${
                lockedForBirth ? 'locked' : kind
              }`}
            >
              <div className="pilot-check-header">
                <h3 className="pilot-check-title">{title}</h3>
                <p className="pilot-check-hint">{hint}</p>
              </div>
              {lockedForBirth ? (
                <p className="pilot-check-locked-hint">
                  Сначала укажите дату рождения
                </p>
              ) : (
                <>
                  <p
                    className={`pilot-check-status-line${
                      kind === 'overdue' ? ' is-overdue' : ''
                    }`}
                  >
                    {statusText(item, dateFormat, itemKind)}
                  </p>
                  {canEdit ? (
                    <div className="pilot-check-form">
                      <input
                        type="date"
                        value={drafts[key]}
                        max={getToday()}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        aria-label={`${dateLabel}: ${title}`}
                      />
                      <button
                        type="button"
                        className="btn-check-save"
                        disabled={saving === key}
                        onClick={() => handleSaveCheck(key)}
                      >
                        {saving === key ? '…' : 'Сохранить'}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default PilotChecksPanel;
